import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { LoadingCard } from "../components/LoadingSpinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Users,
  FolderOpen,
  DollarSign,
  Calendar,
  TrendingUp,
  Plus,
  Clock,
  CheckCircle2,
  AlertCircle,
  FileText,
  PhoneCall,
} from "lucide-react";
import { reportService } from "../services/reportService";
import { projectService } from "../services/projectService";
import { scheduleService } from "../services/scheduleService";
import { projectStatusService } from "../services/projectStatusService";
import { callbackService } from "../services/callbackService";
import { useQuery } from "@tanstack/react-query";

export default function Dashboard() {
  // State for Active Projects dialog
  const [isActiveProjectsDialogOpen, setIsActiveProjectsDialogOpen] = React.useState(false);
  // State for Callbacks-to-Schedule dialog
  const [isCallbacksDialogOpen, setIsCallbacksDialogOpen] = React.useState(false);

  // Calculate date range for next 7 days
  const getDateRange = () => {
    const today = new Date();
    const nextWeek = new Date();
    nextWeek.setDate(today.getDate() + 7);
    
    return {
      date_from: today.toISOString().split('T')[0],
      date_to: nextWeek.toISOString().split('T')[0]
    };
  };

  // Fetch dashboard data from API
  const { data: dashboardStats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => reportService.getDashboard()
  });

  const { data: projectsData, isLoading: projectsLoading } = useQuery({
    queryKey: ['dashboard-projects'],
    queryFn: () => projectService.getAll({ limit: 5 })
  });

  const { data: scheduleData, isLoading: scheduleLoading } = useQuery({
    queryKey: ['dashboard-schedule-today'],
    queryFn: () => scheduleService.getTodaySummary()
  });

  const { data: upcomingScheduleData, isLoading: upcomingLoading } = useQuery({
    queryKey: ['dashboard-schedule-upcoming'],
    queryFn: () => scheduleService.getAll({ ...getDateRange(), limit: 10 })
  });

  // Fetch active projects when dialog is opened
  const { data: activeProjectsData, isLoading: activeProjectsLoading } = useQuery({
    queryKey: ['active-projects-list'],
    queryFn: () => reportService.getActiveProjects(),
    enabled: isActiveProjectsDialogOpen
  });

  // Fetch open callbacks (needing to be scheduled) — always so the card shows the count
  const { data: openCallbacksData } = useQuery({
    queryKey: ['dashboard-open-callbacks'],
    queryFn: () => callbackService.getAll({ status: 'open', limit: 100 })
  });
  const openCallbacks = openCallbacksData?.data || [];
  const openCallbacksCount = openCallbacksData?.pagination?.total ?? openCallbacks.length;

  const { data: projectStatusesData } = useQuery({
    queryKey: ['project-statuses'],
    queryFn: () => projectStatusService.getAll()
  });

  // Extract project statuses with fallback
  const projectStatuses = React.useMemo(() => {
    if (projectStatusesData?.data) {
      return projectStatusesData.data.map(status => ({
        value: status.value,
        label: status.label,
        color: status.color
      }));
    }
    // Fallback for loading state
    return [
      { value: "design_creation", label: "Design Creation", color: "bg-blue-500" }
    ];
  }, [projectStatusesData]);

  // Extract stats with fallbacks
  const stats = dashboardStats?.data || {};
  const estimatedRevenue = stats.estimated_revenue || 0;
  const monthlyRevenueCollected = stats.monthly_revenue_collected || 0;
  const totalProjects = stats.total_projects || 0;
  const activeProjects = stats.active_projects || 0;

  // Get recent projects
  const recentProjects = projectsData?.data?.slice(0, 3) || [];
  
  // Get upcoming tasks from real schedule data (next 7 days)
  const upcomingTasks = React.useMemo(() => {
    if (!upcomingScheduleData?.data || !Array.isArray(upcomingScheduleData.data)) {
      return [];
    }
    
    const scheduleItems = upcomingScheduleData.data;
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    return scheduleItems
      .filter(item => {
        // Filter items that have valid dates and are in the future or today
        if (!item.date) return false;
        const itemDate = new Date(item.date);
        return itemDate >= todayStart;
      })
      .sort((a, b) => {
        // Sort by date, then by time
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        if (dateA.getTime() !== dateB.getTime()) {
          return dateA - dateB;
        }
        // If same date, sort by start_time
        const timeA = a.start_time || '00:00';
        const timeB = b.start_time || '00:00';
        return timeA.localeCompare(timeB);
      })
      .slice(0, 5) // Show only next 5 tasks
      .map(item => ({
        id: item.id,
        title: item.title,
        customer: item.customer,
        date: item.date,
        startTime: item.start_time || '09:00',
        status: item.status || 'scheduled',
        type: item.type,
        project: item.project,
        address: item.address
      }));
  }, [upcomingScheduleData]);

  const getStatusColor = (status) => {
    const statusObj = projectStatuses.find(s => s.value === status);
    return statusObj ? statusObj.color : "bg-gray-500";
  };

  const getStatusLabel = (status) => {
    const statusObj = projectStatuses.find(s => s.value === status);
    return statusObj ? statusObj.label : status;
  };

  if (statsLoading) {
    return <LoadingCard>Loading dashboard...</LoadingCard>;
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">Welcome back! Here's what's happening with your landscape business.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 shrink-0">
          <Button
            variant="outline"
            onClick={() => window.location.href = '/schedule'}
            className="w-full sm:w-auto"
          >
            <Calendar className="mr-2 h-4 w-4" />
            Schedule
          </Button>
          <Button
            className="bg-brand-600 hover:bg-brand-700 w-full sm:w-auto"
            onClick={() => window.location.href = '/projects'}
          >
            <Plus className="mr-2 h-4 w-4" />
            New Project
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <Card className="p-0">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 p-3 sm:p-6 pb-1 sm:pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium">Estimated Revenue</CardTitle>
            <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-brand-600" />
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
            <div className="text-xl sm:text-2xl font-bold">${estimatedRevenue.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Total job cost of all projects
            </p>
          </CardContent>
        </Card>
        <Card
          className="p-0 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => setIsActiveProjectsDialogOpen(true)}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 p-3 sm:p-6 pb-1 sm:pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium">Active Projects</CardTitle>
            <FolderOpen className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
            <div className="text-xl sm:text-2xl font-bold">{activeProjects}</div>
            <p className="text-xs text-muted-foreground">
              Click to view details
            </p>
          </CardContent>
        </Card>
        <Card
          className="p-0 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => setIsCallbacksDialogOpen(true)}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 p-3 sm:p-6 pb-1 sm:pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium">Callbacks to Schedule</CardTitle>
            <PhoneCall className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
            <div className="text-xl sm:text-2xl font-bold">{openCallbacksCount}</div>
            <p className="text-xs text-muted-foreground">
              {openCallbacksCount === 0 ? 'All callbacks scheduled' : 'Click to view list'}
            </p>
          </CardContent>
        </Card>
        <Card className="p-0">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 p-3 sm:p-6 pb-1 sm:pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium">Monthly Revenue Collected</CardTitle>
            <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
            <div className="text-xl sm:text-2xl font-bold">${monthlyRevenueCollected.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Payments collected this month
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Recent Projects */}
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center text-base sm:text-lg">
              <FolderOpen className="mr-2 h-4 w-4 sm:h-5 sm:w-5 text-brand-600" />
              Recent Projects
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Latest project updates and status
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
            <div className="space-y-3 sm:space-y-4">
              {recentProjects.map((project) => (
                <div key={project._id || project.id} className="flex items-center justify-between p-3 sm:p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start sm:items-center justify-between gap-2 flex-col sm:flex-row">
                      <h4 className="font-medium text-sm sm:text-base text-gray-900 truncate">{project.name}</h4>
                      <Badge className={`${getStatusColor(project.status)} text-white`}>
                        {getStatusLabel(project.status)}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{project.customer}</p>
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                        <span>Progress</span>
                        <span>{project.progress}%</span>
                      </div>
                      <Progress value={project.progress} className="h-2" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <Button 
              variant="outline" 
              className="w-full mt-4"
              onClick={() => window.location.href = '/projects'}
            >
              View All Projects
            </Button>
          </CardContent>
        </Card>

        {/* Upcoming Tasks */}
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center text-base sm:text-lg">
              <Clock className="mr-2 h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
              Upcoming Tasks
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Your schedule for the next 7 days
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
            {upcomingLoading ? (
              <div className="space-y-4">
                <div className="flex items-center space-x-3 p-3 border rounded-lg animate-pulse">
                  <div className="w-5 h-5 bg-gray-300 rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            ) : upcomingTasks.length === 0 ? (
              <div className="text-center py-6 sm:py-8">
                <Clock className="h-12 w-12 sm:h-16 sm:w-16 text-gray-300 mx-auto mb-3 sm:mb-4" />
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No Upcoming Tasks</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4">No scheduled tasks found for the next 7 days.</p>
                <Button 
                  onClick={() => window.location.href = '/schedule'}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Schedule Item
                </Button>
              </div>
            ) : (
              <>
                <div className="space-y-3 sm:space-y-4">
                  {upcomingTasks.map((task) => {
                    const taskDate = new Date(task.date);
                    const today = new Date();
                    const isToday = taskDate.toDateString() === today.toDateString();
                    const isTomorrow = taskDate.toDateString() === new Date(today.getTime() + 24 * 60 * 60 * 1000).toDateString();
                    
                    // Get relative date label
                    let dateLabel;
                    if (isToday) {
                      dateLabel = "Today";
                    } else if (isTomorrow) {
                      dateLabel = "Tomorrow";
                    } else {
                      dateLabel = taskDate.toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric' 
                      });
                    }

                    // Get type color and icon
                    const getTypeColor = (type) => {
                      switch (type) {
                        case 'project': return 'text-brand-600 bg-emerald-50';
                        case 'customer': return 'text-blue-600 bg-blue-50';
                        case 'lead': return 'text-orange-600 bg-orange-50';
                        default: return 'text-gray-600 bg-gray-50';
                      }
                    };

                    const getStatusIcon = (status) => {
                      switch (status) {
                        case "completed":
                          return <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />;
                        case "in_progress":
                          return <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />;
                        case "cancelled":
                          return <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />;
                        default:
                          return <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />;
                      }
                    };

                    return (
                      <div key={task.id} className="flex items-start space-x-2 sm:space-x-3 p-2.5 sm:p-3 border rounded-lg hover:bg-gray-50 transition-colors">
                        <div className="flex-shrink-0 mt-1">
                          {getStatusIcon(task.status)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{task.title}</p>
                              <p className="text-sm text-gray-600">{task.customer}</p>
                              {task.project && (
                                <p className="text-xs text-gray-500">Project: {task.project}</p>
                              )}
                            </div>
                            <div className="ml-2 flex-shrink-0">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(task.type)}`}>
                                {task.type?.charAt(0).toUpperCase() + task.type?.slice(1) || 'Task'}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center mt-1.5 sm:mt-2 text-[10px] sm:text-xs text-gray-500">
                            <Calendar className="mr-1 h-3 w-3 flex-shrink-0" />
                            <span className={isToday ? "font-medium text-blue-600" : ""}>
                              {dateLabel} at {task.startTime}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <Button 
                  variant="outline" 
                  className="w-full mt-4"
                  onClick={() => window.location.href = '/schedule'}
                >
                  View Full Schedule
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-base sm:text-lg">Quick Actions</CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            Common tasks to get things done faster
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            <Button
              variant="outline"
              className="h-16 sm:h-20 flex-col text-xs sm:text-sm min-h-[2.5rem]"
              onClick={() => window.location.href = '/leads'}
            >
              <Plus className="h-5 w-5 sm:h-6 sm:w-6 mb-1.5 sm:mb-2" />
              Add Lead
            </Button>
            <Button
              variant="outline"
              className="h-16 sm:h-20 flex-col text-xs sm:text-sm min-h-[2.5rem]"
              onClick={() => window.location.href = '/estimates'}
            >
              <FileText className="h-5 w-5 sm:h-6 sm:w-6 mb-1.5 sm:mb-2" />
              Create Estimate
            </Button>
            <Button
              variant="outline"
              className="h-16 sm:h-20 flex-col text-xs sm:text-sm min-h-[2.5rem]"
              onClick={() => window.location.href = '/schedule'}
            >
              <Calendar className="h-5 w-5 sm:h-6 sm:w-6 mb-1.5 sm:mb-2" />
              Schedule Visit
            </Button>
            <Button
              variant="outline"
              className="h-16 sm:h-20 flex-col text-xs sm:text-sm min-h-[2.5rem]"
              onClick={() => window.location.href = '/customers'}
            >
              <Users className="h-5 w-5 sm:h-6 sm:w-6 mb-1.5 sm:mb-2" />
              Add Customer
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Active Projects Dialog */}
      <Dialog open={isActiveProjectsDialogOpen} onOpenChange={setIsActiveProjectsDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto w-[calc(100%-2rem)] sm:w-auto rounded-lg">
          <DialogHeader>
            <DialogTitle>Active Projects</DialogTitle>
            <DialogDescription>
              All projects that are not completed, cancelled, or on hold
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {activeProjectsLoading ? (
              <div className="text-center py-4">Loading...</div>
            ) : activeProjectsData?.data?.length > 0 ? (
              <div className="space-y-3">
                {activeProjectsData.data.map((project) => (
                  <div
                    key={project.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 border rounded-lg hover:bg-gray-50 gap-2 sm:gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm sm:text-base truncate">{project.name}</div>
                      <div className="text-xs sm:text-sm text-gray-500 flex items-center gap-2 sm:gap-3 flex-wrap">
                        <Badge variant="outline" className="text-xs">
                          {project.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Badge>
                        {project.project_type && (
                          <span className="text-xs">
                            {project.project_type === 'construction' ? 'Construction' : 'Design Only'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-left sm:text-right">
                      <div className="text-xs sm:text-sm text-gray-500">Amount Owed</div>
                      <div className={`font-semibold text-sm sm:text-base ${project.amount_owed > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                        ${project.amount_owed?.toLocaleString() || '0'}
                      </div>
                    </div>
                  </div>
                ))}
                <div className="pt-4 border-t">
                  <div className="flex justify-between text-sm font-medium">
                    <span>Total Active Projects:</span>
                    <span>{activeProjectsData.data.length}</span>
                  </div>
                  <div className="flex justify-between text-sm font-medium mt-1">
                    <span>Total Amount Owed:</span>
                    <span className="text-orange-600">
                      ${activeProjectsData.data.reduce((sum, p) => sum + (p.amount_owed || 0), 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500">No active projects found</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Callbacks to Schedule Dialog */}
      <Dialog open={isCallbacksDialogOpen} onOpenChange={setIsCallbacksDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto w-[calc(100%-2rem)] sm:w-auto rounded-lg">
          <DialogHeader>
            <DialogTitle>Callbacks Needing to Be Scheduled</DialogTitle>
            <DialogDescription>
              Open callbacks that haven't been assigned to a crew or scheduled yet
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {openCallbacks.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-2" />
                No callbacks waiting to be scheduled
              </div>
            ) : (
              <>
                {openCallbacks.map((cb) => {
                  const urgencyColor =
                    cb.urgency === 'urgent' ? 'bg-red-600 text-white' :
                    cb.urgency === 'high' ? 'bg-orange-500 text-white' :
                    cb.urgency === 'low' ? 'bg-gray-300 text-gray-800' :
                    'bg-yellow-500 text-white';
                  const id = cb.id || cb._id;
                  return (
                    <div
                      key={id}
                      className="p-3 sm:p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => {
                        setIsCallbacksDialogOpen(false);
                        window.location.href = `/callbacks?openId=${id}`;
                      }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm sm:text-base truncate">
                            {cb.customer_name || 'Unknown Customer'}
                            {cb.project_name && <span className="text-gray-500 font-normal"> — {cb.project_name}</span>}
                          </div>
                          {cb.jobsite_address && (
                            <div className="text-xs sm:text-sm text-gray-500 truncate">{cb.jobsite_address}</div>
                          )}
                          <div className="text-xs sm:text-sm text-gray-700 mt-1 line-clamp-2">
                            {cb.issue_description}
                          </div>
                        </div>
                        <Badge className={`${urgencyColor} shrink-0 capitalize`}>
                          {cb.urgency || 'normal'}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
                <div className="pt-2 border-t">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => { setIsCallbacksDialogOpen(false); window.location.href = '/callbacks'; }}
                  >
                    View All Callbacks
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}